import {ISortingContext} from 'plugins/modules/sorting/context';
import React, {FC, useCallback, useMemo} from 'react';
import {InputSelect, ISelectOption, ISelectProps} from 'components/InputSelect';
import {Sorting} from 'models';
import style from './style.module.scss';
import {Typography} from 'components/Typography';
import {components, ControlProps, OptionProps} from 'react-select';
import classNames from 'classnames';
import Icon from 'components/Icon';
import {isMobileOrTabletByScreen} from 'core/helpers';

export interface ISortingSelectProps<T = Sorting['param']> {
    sortingList: ISortingContext['sortingList'];
    className?: string;
    isLoading?: boolean;
    onSortChange?: ISelectProps<T>['onChange'];
}

export function SortingSelect<T = Sorting['param']>({
    isLoading,
    className,
    sortingList,
    onSortChange,
}: ISortingSelectProps<T>) {
    const isTabletOrMobile = isMobileOrTabletByScreen();
    const sortOptions = useMemo<ISelectProps<T>['options']>(() => {
        return (
            sortingList
                // на мобилке согласно макетам мы должны выводить применённую сортировку вверху общего списка
                .filter((item) => (isTabletOrMobile ? true : !item.isApplied))
                .sort((a, b) => {
                    if (a.isApplied && !b.isApplied) {
                        return -1;
                    }
                    if (!a.isApplied && b.isApplied) {
                        return 1;
                    }
                    return 0;
                })
                .map((sortItem) => {
                    return {
                        label: sortItem.title,
                        value: sortItem.param,
                    } as ISelectOption<T>;
                })
        );
    }, [isTabletOrMobile, sortingList]);

    const appliedSort = useMemo(() => sortingList.find(({isApplied}) => isApplied), [sortingList]);

    const defaultSort = useMemo<ISelectProps<T>['value']>(() => {
        if (!appliedSort) {
            return {
                label: sortingList[0]?.title,
                value: sortingList[0]?.param,
            } as ISelectOption<T>;
        }

        return {
            label: appliedSort.title,
            value: appliedSort.param,
        } as ISelectOption<T>;
    }, [appliedSort, sortingList]);

    const option = useCallback<FC<OptionProps>>(
        (props) => {
            const {children, data} = props;
            const text = sortingList?.find(({param}) => param === (data as ISelectOption<T>)?.value)?.comment ?? '';
            return (
                <components.Option {...props}>
                    <Typography variant={'body-16-r'}>{children}</Typography>
                    {text && (
                        <Typography
                            className={classNames(style.optionText, 'option-text')}
                            element={'div'}
                            variant={'p-small'}
                        >
                            {text}
                        </Typography>
                    )}
                </components.Option>
            );
        },
        [sortingList],
    );

    const control = useCallback<FC<ControlProps>>(
        ({children, selectProps, ...props}: ControlProps) => (
            <components.Control className={style.control} selectProps={selectProps} {...props}>
                <>{children}</>
            </components.Control>
        ),
        [],
    );

    return (
        <InputSelect<T>
            className={classNames(className, 'sorting-select', style.select)}
            customControl={control}
            customOption={option}
            disabled={isLoading}
            dropDownIndicatorIcon={<Icon className={style.sortingIcon} icon={'sorting-icon'} />}
            emptyMessage={'Не найдено'}
            onChange={onSortChange}
            options={sortOptions}
            value={defaultSort}
            variant={'filter'}
        />
    );
}
